// Utilitaires de binding « valeur → champ canonique » du formulaire simulateur.
//
// Mutualisés entre :
// - `profiles_controller.js` (panneau « Mes profils », #290/#306) qui applique
//   l'intégralité des critères d'un profil chargé (`applyResponse`) ;
// - `profile_wizard_controller.js` (wizard multi-écrans, #307) qui pousse les
//   champs d'un écran dans les champs canoniques à chaque étape.
//
// Source de vérité unique : ces helpers écrivent dans le champ canonique
// (`#edit-profile-form`) puis laissent les pipelines existants (`edit-profile`
// pour la persistance, `api-sync` pour le recalcul) réagir aux évènements
// `input`/`change`.

// Échappe une valeur pour l'utiliser dans un sélecteur CSS (`[name="..."]`).
export function cssEscape(value) {
    if (typeof window !== "undefined" && window.CSS && typeof window.CSS.escape === "function") {
        return window.CSS.escape(value);
    }
    return String(value).replace(/["\\\]]/g, "\\$&");
}

// Pose `value` sur le(s) champ(s) `criterias[key]` du formulaire `form`.
//
// Les champs « number-format » créent un input caché jumeau (même name) : on ne
// pilote que le champ visible et la normalisation synchronise le jumeau. À
// l'inverse, un champ uniquement caché (ex. monthly_incomes) doit être renseigné
// directement.
export function setCriteriaField(form, key, value) {
    setNamedField(form, `criterias[${key}]`, value);
}

// Variante par nom complet du champ (ex. `profile[investment_mode]`).
export function setNamedField(form, name, value) {
    const selector = `[name="${cssEscape(name)}"]`;
    const elements = Array.from(form.querySelectorAll(selector));
    if (elements.length === 0) return;

    const visible = elements.filter((el) => el.type !== "hidden");
    const targets = visible.length > 0 ? visible : elements;
    targets.forEach((el) => setElementValue(el, value));
}

// Pose une valeur sur un élément de formulaire en gérant les types
// (checkbox/radio/select/text) et la normalisation `number-format`.
export function setElementValue(el, value) {
    if (el.type === "checkbox") {
        el.checked = Boolean(value);
        return;
    }
    if (el.type === "radio") {
        el.checked = String(el.value) === String(value);
        return;
    }
    if (el.tagName === "SELECT") {
        setSelectValue(el, value);
        return;
    }

    el.value = value == null ? "" : String(value);
    if ((el.dataset.controller || "").includes("number-format")) {
        try {
            el.dispatchEvent(new Event("number-format:normalize", { bubbles: true }));
        } catch (_) {}
    }
}

// Pose une valeur sur un `<select>` en tolérant les écarts de format numérique
// entre la valeur poussée et celle des `<option>`.
//
// Le wizard pousse le nombre de parts sous forme entière (`"2"`, `"3"`…) alors
// que les options du select « Nombre de parts » sont des Float (`"2.0"`,
// `"3.0"`…). Une affectation par égalité stricte de chaîne (`el.value = "2"`)
// ne matche alors aucune option et laisse le select vide (cf. #449). On tente
// d'abord l'affectation directe, puis on retombe sur une comparaison numérique
// des `<option>` si aucune option n'a été sélectionnée.
export function setSelectValue(el, value) {
    if (value == null) {
        el.value = "";
        return;
    }

    const str = String(value);
    el.value = str;
    if (el.value === str && el.selectedIndex !== -1) return;

    const target = parseFloat(str);
    if (Number.isNaN(target)) return;

    const match = Array.from(el.options).find((opt) => parseFloat(opt.value) === target);
    if (match) el.value = match.value;
}

// Rejoue le recalcul « Salaires annuels nets = 12 × revenus mensuels »
// (`household-incomes#recompute`) après une écriture programmatique des champs
// canoniques (#583).
//
// Le contrôleur `household-incomes` n'est branché que sur l'`input` du champ
// mensuel (saisie manuelle) : le wizard et le panneau « Mes profils » posent la
// valeur sans déclencher cet évènement, et le champ readonly annuel restait
// alors sur la valeur du profil (80 000 € par défaut). L'évènement est
// volontairement non bouillonnant : c'est `recompute` qui propage ensuite depuis
// le champ annuel (persistance + recalcul d'impôt).
export function syncHouseholdIncomes(form) {
    if (!form) return;
    const monthly = form.querySelector('[data-household-incomes-target="monthly"]');
    if (!monthly) return;
    try {
        monthly.dispatchEvent(new Event("input"));
    } catch (_) {}
}

// Lit la valeur « brute » d'un champ canonique (en privilégiant `dataset.raw`
// posé par `number-format`, sinon la valeur affichée).
export function readNamedFieldValue(form, name) {
    const selector = `[name="${cssEscape(name)}"]`;
    const elements = Array.from(form.querySelectorAll(selector));
    if (elements.length === 0) return null;

    const radio = elements.find((el) => el.type === "radio");
    if (radio) {
        const checked = elements.find((el) => el.type === "radio" && el.checked);
        return checked ? checked.value : null;
    }

    const visible = elements.filter((el) => el.type !== "hidden");
    const el = visible.length > 0 ? visible[0] : elements[0];
    if (el.dataset && el.dataset.raw !== undefined && el.dataset.raw !== "") return el.dataset.raw;
    return el.value;
}
